import React from 'react';
import { Panel } from '@/components/ui/panel';
import { Link, Text } from '@chakra-ui/react';
import { DocumentWithDownloadUrl as Document } from '@/client';
import BaseDocumentEdit from './BaseDocumentEdit';
import { getNoteTextSegments } from '@/utils/documentUtils.ts';
import { Title } from '@/components/ui/title';

interface DocumentNoteEditProps {
	document: Document;
	onVisibilityChange?: (isPrivate: boolean) => void;
	/** True when the note is a pending share — hides the in-body privacy toggle (privacy is chosen at accept time). */
	isPendingShare?: boolean;
}

/**
 * Read-only display of a note. The first line of the description is rendered
 * as the title (similar to Apple Notes pattern); subsequent lines are the body. Editing
 * happens in DocumentNoteSheet, launched by the Edit button in the panel
 * footer.
 */
const DocumentNoteEdit: React.FC<DocumentNoteEditProps> = ({
	document,
	onVisibilityChange,
	isPendingShare = false,
}) => {
	const content = document.description ?? '';
	const newlineIdx = content.indexOf('\n');
	const title = (newlineIdx === -1 ? content : content.slice(0, newlineIdx)).trim();
	const body = newlineIdx === -1 ? '' : content.slice(newlineIdx + 1).trim();
	const hasTitle = title.length > 0;
	const hasBody = body.length > 0;
	const renderLinkedText = (text: string) =>
		getNoteTextSegments(text).map((segment, index) => {
			if (segment.type === 'text') return <React.Fragment key={index}>{segment.text}</React.Fragment>;

			return (
				<Link key={index} href={segment.href} target="_blank" rel="noopener noreferrer" color="brand.solid">
					{segment.text}
				</Link>
			);
		});

	return (
		<BaseDocumentEdit document={document} onVisibilityChange={onVisibilityChange} isPendingShare={isPendingShare}>
			<Panel minH={40} p={4}>
				{hasTitle ? (
					<Title level="dialog" color="brand.fg" lineHeight="1.3">
						{renderLinkedText(title)}
					</Title>
				) : (
					<Text fontSize="xl" color="fg.muted" fontStyle="italic">
						Empty note
					</Text>
				)}
				{hasBody && (
					<Text whiteSpace="pre-wrap" textStyle="body" color="brand.fg" lineHeight={1.625} mt={3}>
						{renderLinkedText(body)}
					</Text>
				)}
			</Panel>
		</BaseDocumentEdit>
	);
};

export default DocumentNoteEdit;
